'use client';

/**
 * useFaqQuery Hook
 *
 * Custom hook for managing FAQ query form state and analytics.
 */
import { useState } from 'react';

import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface UseFaqQueryOptions {
  /**
   * Optional initial show form state
   */
  initialShow?: boolean;

  /**
   * Optional callback when form is toggled
   */
  onToggle?: (show: boolean) => void;
}

/**
 * Custom hook for managing FAQ query form state
 *
 * @example
 * ```tsx
 * const { showForm, toggleForm, isMobile } = useFaqQuery();
 * ```
 */
export function useFaqQuery(options: UseFaqQueryOptions = {}) {
  const { initialShow = false, onToggle } = options;
  const [showForm, setShowForm] = useState(initialShow);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const toggleForm = () => {
    const newState = !showForm;
    setShowForm(newState);
    if (onToggle) {
      onToggle(newState);
    }
    // Could add analytics tracking here
  };

  return {
    showForm,
    setShowForm,
    toggleForm,
    isMobile
  };
}
